import { debounce } from 'lodash';
import Page from 'modules/app/components/Page';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useActions } from 'store/useActions';
import { useQueryClient } from '@tanstack/react-query';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import useProfile from 'sdk/hooks/user/useProfile';
import styles from './Feed.module.scss';
import PostBlock from './PostBlock';
import NoContentFound from './NoContentFound';
import TopMenuWithSearch from './TopMenuWithSearch';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { supabase } from 'sdk/api';
import { useMemo } from 'react';
import useAllPosts, { PostFilter } from 'sdk/hooks/useAllPosts';
import { useTypedSelector } from 'store/useTypedSelector';

function Feed() {
    const { postFilter } = useTypedSelector(state => state.feedSlice);
    const feedRef = useRef(null);
    const [, setFeedGroup] = useState('global');
    const prevDeltaY = useRef(0);
    const prev2DeltaY = useRef(0);
    const prev3DeltaY = useRef(0);
    const wheelEventTimeLast = useRef(new Date().getTime());
    const wheelEventTimeLastForTouchPad = useRef(new Date().getTime());
    const [videoMuted, setVideoMuted] = useState(false);
    const [wheelStatus, setWheelStatus] = useState<string | null>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isGoBack, setIsGoBack] = useState(true);
    const { isAppointmentBookingOpen } = useTypedSelector(state => state.postSlice);
    const { setFeedStateId, setIsSlideChange } = useActions();

    const { id, feedId, feedName } = useParams();
    const navigate = useNavigate();

    const { data: profileData } = useProfile();
    const { data: rawPosts } = useAllPosts(postFilter);

    const posts = useMemo(() => {
        if (!rawPosts?.length) return [];

        if (id) {
            // Find the post with matching id
            const targetPost = rawPosts.find(post => post.data.id === id);
            if (!targetPost) return rawPosts;

            // Filter out the target post from other posts
            const otherPosts = rawPosts.filter(post => post.data.id !== id);

            // Return array with target post first, followed by other posts
            return [targetPost, ...otherPosts];
        }
        return rawPosts;
    }, [rawPosts, id]);

    useEffect(() => {
        if (window.analytics) {
            window.analytics.track('postViewed', {
                userId: profileData?.id,
                postId: posts?.[currentSlide]?.data?.id,
            });
        }
    }, [currentSlide, posts, profileData]);

    const setSlideIdToUrl = () => {
        // @ts-ignore
        const lastPost = feedRef.current?.swiper?.activeIndex;
        const activeSlide = document.querySelector(`.swiper-slide:nth-child(${lastPost + 1 || 1})`);
        // @ts-ignore
        const activeId = activeSlide?.dataset?.id;

        const basePath = feedName && feedId ? `/user/feed/${feedName}/${feedId}` : '/feed';
        const finalPath = activeId ? `${basePath}/${activeId}` : basePath;

        window.history.replaceState(null, '', finalPath);
    };

    const onSlideChange = () => {
        setIsSlideChange(true);
        setSlideIdToUrl();
    };

    const handleNextPage = useCallback(() => {
        console.log('handleNextPage');
    }, []);

    const handlePrevPage = useCallback(() => {
        console.log('handlePrevPage');
    }, []);

    const debouncedOnWheel = debounce(
        status => {
            if (status === 'down' && currentSlide === 0) {
                navigate('/feed');
                setFeedStateId(Math.round(Math.random() * 10000));
            }
            setWheelStatus(status);
            setTimeout(() => {
                setWheelStatus(null);
            }, 300);
        },
        310,
        {
            leading: true,
            trailing: false,
        },
    );

    const OnWheel = (event: React.WheelEvent<HTMLDivElement>) => {
        const { deltaY } = event;

        const currentTime = new Date().getTime();

        if (currentTime - wheelEventTimeLast.current > 500) {
            if (deltaY > 0) {
                debouncedOnWheel('up');
            } else {
                debouncedOnWheel('down');
            }
            wheelEventTimeLastForTouchPad.current = new Date().getTime();
        } else if (currentTime - wheelEventTimeLastForTouchPad.current > 500) {
            if (
                (deltaY > 0 &&
                    deltaY > prevDeltaY.current &&
                    prevDeltaY.current > prev2DeltaY.current &&
                    prev2DeltaY.current > prev3DeltaY.current) ||
                (deltaY < 0 &&
                    deltaY < prevDeltaY.current &&
                    prevDeltaY.current < prev2DeltaY.current &&
                    prev2DeltaY.current < prev3DeltaY.current)
            ) {
                debouncedOnWheel(deltaY > 0 ? 'up' : 'down');
            }
            prev3DeltaY.current = prev2DeltaY.current;
            prev2DeltaY.current = prevDeltaY.current;
            prevDeltaY.current = deltaY;
        }

        wheelEventTimeLast.current = currentTime;
    };

    const handleSaveCurrentState = () => {
        sessionStorage.setItem('feedCurrentIndex', currentSlide.toString());
    };

    useEffect(() => {
        window.addEventListener('load', () => {
            setIsGoBack(false);
        });

        return () => {
            window.removeEventListener('load', () => {
                setIsGoBack(false);
            });
        };
    }, []);

    const handleKeyDownUp = event => {
        if (event.key === 'ArrowDown') {
            // @ts-ignore
            feedRef.current?.swiper?.slideNext();
        } else if (event.key === 'ArrowUp') {
            // @ts-ignore
            feedRef.current?.swiper?.slidePrev();
        }
    };
    // console.log(isAppointmentBookingOpen)
    return !posts || posts.length === 0 ? (
        <NoContentFound feedData={posts?.length} setFeedGroup={setFeedGroup} />
    ) : (
        <div
            className={styles.container}
            onWheel={event => {
                event.stopPropagation();
                OnWheel(event);
            }}
        >
            <Swiper
                className={styles.posts}
                ref={feedRef}
                direction="vertical"
                slidesPerView={1}
                centeredSlides
                initialSlide={0}
                spaceBetween={0}
                speed={200}
                effect="slide"
                onSlideChange={onSlideChange}
                // onTouchStart={() => {
                //   setIsFirstLoaded(false)
                // }}
                onTouchEnd={swiper => {
                    if (swiper.activeIndex === 0 && swiper.touches.diff > 0) {
                        // navigate('/feed')
                        // setFeedStateId(Math.round(Math.random() * 10000))
                    }
                }}
                touchStartPreventDefault={false}
                breakpoints={{
                    501: {
                        slidesPerView: 'auto',
                        spaceBetween: 10,
                        autoHeight: true,
                    },
                    1401: {
                        slidesPerView: 'auto',
                        spaceBetween: 20,
                        autoHeight: true,
                    },
                }}
                allowSlideNext={isAppointmentBookingOpen}
                allowSlidePrev={isAppointmentBookingOpen}
            >
                {posts?.length
                    ? posts.map((post: any, idx: number) => (
                        <SwiperSlide key={post?.data?.id} className={styles.postContainer} data-id={post?.data?.id}>
                            {({ isActive, isNext, isPrev }) => (
                                <PostBlock
                                    data={post}
                                    targetPostId={id}
                                    setCategoryFilter={setFeedGroup}
                                    videoMuted={videoMuted}
                                    onVideoMuted={setVideoMuted}
                                    onNextPage={handleNextPage}
                                    onPrevPage={handlePrevPage}
                                    onSaveCurrentState={handleSaveCurrentState}
                                    onSetCurrentSlide={setCurrentSlide}
                                    idx={idx}
                                    isActive={isActive}
                                />
                            )}
                        </SwiperSlide>
                    ))
                    : null}
            </Swiper>
        </div>
    );
}

function FeedPage() {
    const { data: isAuthData } = useIsAuth();
    const { setIsButtonMenuVisible } = useActions();
    const isCreatingProfile = useRef(false); // Флаг для предотвращения двойного создания
    const queryClient = useQueryClient(); // Для инвалидации кэша

    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add('overflow-hidden');

        return () => {
            document.body.classList.remove('overflow-hidden');
        };
    });

    useEffect(() => {
        const checkUserIfProviderIsEmail = async () => {
            // Защита от двойного выполнения
            if (isCreatingProfile.current) {
                console.log('⚠️ Profile creation already in progress, skipping...');
                return;
            }

            if (isAuthData?.isAuth && isAuthData?.user?.app_metadata?.provider === 'google') {
                isCreatingProfile.current = true;

                try {
                    console.log('🔍 Checking Google user profile for:', isAuthData?.user?.id);

                    // Используем maybeSingle() для безопасной проверки
                    const { data: userData, error: selectError } = await supabase
                        .from('user_profiles')
                        .select('*')
                        .eq('id', isAuthData?.user?.id)
                        .maybeSingle();

                    if (selectError) {
                        console.error('❌ Error checking user profile:', selectError);
                        return;
                    }

                    if (!userData) {
                        console.log('📝 User profile not found, creating new profile...');

                        // Защитные механизмы: приоритет основного email, fallback для всех полей
                        const email = isAuthData?.user?.email || 
                                     isAuthData?.user?.user_metadata?.email || 
                                     isAuthData?.user?.user_metadata?.email_verified;
                        
                        const username = isAuthData?.user?.user_metadata?.full_name || 
                                        isAuthData?.user?.user_metadata?.name ||
                                        email?.split('@')[0] || 
                                        'User';
                        
                        const avatar = isAuthData?.user?.user_metadata?.avatar_url || 
                                      isAuthData?.user?.user_metadata?.picture;

                        // Проверка обязательных полей
                        if (!email) {
                            console.error('❌ Cannot create profile: email is missing');
                            alert('Registration error: email not provided by Google. Please try again.');
                            await supabase.auth.signOut();
                            navigate('/sign-in');
                            return;
                        }

                        // Логирование для диагностики
                        console.log('📊 Creating profile with data:', {
                            userId: isAuthData?.user?.id,
                            email,
                            username,
                            hasAvatar: !!avatar,
                        });

                        // Разбиваем full_name на first_name и last_name
                        const nameParts = username.trim().split(/\s+/);
                        const firstName = nameParts[0] || '';
                        const lastName = nameParts.slice(1).join(' ') || '';

                        // Вставляем с возвратом данных для проверки
                        const { data: insertedData, error: errorInsert } = await supabase
                            .from('user_profiles')
                            .insert({
                                id: isAuthData?.user?.id,
                                email: email,
                                username: username,
                                avatar: avatar,
                                first_name: firstName,
                                last_name: lastName,
                            })
                            .select()
                            .single();

                        if (errorInsert) {
                            console.error(
                                '❌ Error while creating new account by email provider:',
                                errorInsert.message,
                                errorInsert
                            );
                            
                            // Показываем пользователю понятное сообщение
                            alert('Registration error: Could not create your profile. Please try again or contact support.');
                            
                            // Выходим из аккаунта и возвращаем на страницу входа
                            await supabase.auth.signOut();
                            navigate('/sign-in');
                            return;
                        }

                        console.log('✅ User profile created successfully:', insertedData);

                        // Проверяем, что данные действительно записались
                        const { data: verifyData, error: verifyError } = await supabase
                            .from('user_profiles')
                            .select('id, email, username')
                            .eq('id', isAuthData?.user?.id)
                            .maybeSingle();

                        if (verifyError || !verifyData) {
                            console.error('❌ Profile verification failed:', verifyError);
                            alert('Registration error: Profile was not saved correctly. Please try again.');
                            await supabase.auth.signOut();
                            navigate('/sign-in');
                            return;
                        }

                        console.log('✅ Profile verified in database:', verifyData);

                        // Инвалидируем кэш профиля, чтобы данные загрузились заново
                        queryClient.invalidateQueries({ queryKey: ['profile'] });
                        queryClient.invalidateQueries({ queryKey: ['isAuth'] });
                        queryClient.invalidateQueries({ queryKey: ['user'] });

                        // Задержка для гарантии синхронизации
                        await new Promise(resolve => setTimeout(resolve, 500));

                        // Используем navigate вместо window.location.href для сохранения сессии
                        navigate('/profile/edit');
                    } else {
                        console.log('✅ User profile already exists:', userData);
                    }
                } catch (error) {
                    console.error('❌ Unexpected error in checkUserIfProviderIsEmail:', error);
                    alert('An unexpected error occurred during registration. Please try again.');
                    await supabase.auth.signOut();
                    navigate('/sign-in');
                } finally {
                    isCreatingProfile.current = false;
                }
            }
        };

        checkUserIfProviderIsEmail();
    }, [isAuthData, navigate]);

    useEffect(() => {
        setIsButtonMenuVisible(true);
    }, []);

    return (
        <Page activeLink="/feed">
            <Feed />
        </Page>
    );
}

export default FeedPage;
